import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormDatePicker } from '@/components/form/FormDatePicker'
import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import type { StudentCertification } from '@/features/student/api/profile.api'
import { useCertificationMutations } from '@/features/student/hooks/useProfile'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'

const schema = z.object({
  name: z.string().min(1, 'Certification name is required'),
  issuer: z.string().optional(),
  issued_on: z.string().optional(),
  expires_on: z.string().optional(),
  credential_url: z.union([z.url('Enter a valid URL'), z.literal('')]).optional(),
})

type FormValues = z.infer<typeof schema>

function toFormValues(item?: StudentCertification): FormValues {
  return {
    name: item?.name ?? '',
    issuer: item?.issuer ?? '',
    issued_on: item?.issuedOn ?? '',
    expires_on: item?.expiresOn ?? '',
    credential_url: item?.credentialUrl ?? '',
  }
}

export function CertificationSection({ items }: { items: StudentCertification[] }) {
  const [editing, setEditing] = useState<StudentCertification | 'new' | null>(null)
  const [apiError, setApiError] = useState<unknown>(null)
  const { create, update, remove } = useCertificationMutations()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: toFormValues() })

  useEffect(() => {
    reset(toFormValues(editing === 'new' || editing === null ? undefined : editing))
  }, [editing, reset])

  const isPending = create.isPending || update.isPending

  const onSubmit = handleSubmit((values) => {
    setApiError(null)
    const onSettled = { onSuccess: () => setEditing(null), onError: (e: unknown) => setApiError(e) }
    if (editing === 'new') {
      create.mutate(values, onSettled)
    } else if (editing) {
      update.mutate({ id: editing.id, payload: values }, onSettled)
    }
  })

  return (
    <div>
      {items.length === 0 && <p className="text-muted fs-13">No certifications added yet.</p>}
      {items.map((item) => (
        <div
          key={item.id}
          className="d-flex justify-content-between align-items-start border-bottom py-2"
        >
          <div>
            <p className="fw-medium mb-0">{item.name}</p>
            {item.issuer && <p className="fs-12 text-muted mb-0">{item.issuer}</p>}
          </div>
          <div className="d-flex gap-2">
            <Button variant="ghost" onClick={() => setEditing(item)}>
              Edit
            </Button>
            <Button
              variant="danger"
              confirmTitle="Remove this certification?"
              onClick={() => remove.mutate(item.id)}
            >
              Delete
            </Button>
          </div>
        </div>
      ))}
      <Button variant="secondary" className="mt-3" onClick={() => setEditing('new')}>
        Add Certification
      </Button>

      <Modal
        show={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Add Certification' : 'Edit Certification'}
      >
        <FormErrorSummary
          errors={errors}
          serverErrors={getFieldErrors(apiError)}
          generalMessage={apiError ? getErrorMessage(apiError) : null}
        />
        <form onSubmit={(e) => void onSubmit(e)} noValidate>
          <FormInput control={control} name="name" label="Certification Name" required />
          <FormInput control={control} name="issuer" label="Issuer" />
          <FormDatePicker control={control} name="issued_on" label="Issued On" />
          <FormDatePicker control={control} name="expires_on" label="Expires On" />
          <FormInput control={control} name="credential_url" label="Credential URL" type="url" />
          <Button type="submit" className="w-100" loading={isPending}>
            Save
          </Button>
        </form>
      </Modal>
    </div>
  )
}
